/**
 * Admin > Service credentials (1 Nov 2026)
 *
 * The per-instance machine credentials a decision gateway, a DSAR actuation
 * connector, a FOCTTA Core integration or a customer web proxy authenticates
 * with. Until this page, the only way to stop one was to paste a fetch into
 * the browser console (31 Oct 2026, a UAT test credential).
 *
 * This slice lists and revokes. Issue and rotate stay API-only for now: both
 * hand back the raw credential once, which needs its own show-once screen.
 *
 * The list has no secret and no hash; the API never returns either, and the
 * token prefix is all an operator needs to match a row to a config file.
 *
 * Who may revoke which row is mirrored in mayRevokeServiceCredential
 * (lib/service-credentials-api.ts). The button follows it, the route decides.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, Clock, KeyRound, Loader2, RefreshCw, XCircle } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ApiError } from '@/lib/api';
import { useCaller } from '@/lib/access';
import {
  REVOKE_REASON_MAX,
  isRevocable,
  listServiceCredentials,
  mayRevokeServiceCredential,
  revokeServiceCredential,
  type ServiceCredential,
  type ServiceCredentialPurpose,
  type ServiceCredentialStatus,
} from '@/lib/service-credentials-api';

const PURPOSE_LABEL: Record<ServiceCredentialPurpose, string> = {
  decision_gateway: 'Decision gateway',
  dsar_connector: 'DSAR connector',
  core_provider: 'FOCTTA Core provider',
  web_proxy: 'Web proxy',
};

const STATUS_BADGE: Record<ServiceCredentialStatus, { label: string; variant: 'success' | 'warning' | 'critical'; icon: typeof CheckCircle2 }> = {
  active: { label: 'Active', variant: 'success', icon: CheckCircle2 },
  rotating: { label: 'Rotating', variant: 'warning', icon: RefreshCw },
  expired: { label: 'Expired', variant: 'warning', icon: Clock },
  revoked: { label: 'Revoked', variant: 'critical', icon: XCircle },
};

function ist(iso: string | null): string {
  if (!iso) return '-';
  return `${new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

function revokeError(err: unknown): string {
  if (err instanceof ApiError && err.status === 403) {
    return `You are not allowed to revoke this credential. ${err.message}`;
  }
  if (err instanceof ApiError && err.status === 404) {
    return 'This credential is no longer live (it may have been revoked already). Refresh the list to see its state.';
  }
  return `The credential was not revoked: ${err instanceof Error ? err.message : String(err)}`;
}

export function ServiceCredentials(): JSX.Element {
  const access = useCaller();
  const caller = access.status === 'ready' ? access.caller : null;

  const [rows, setRows] = useState<ServiceCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [target, setTarget] = useState<ServiceCredential | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await listServiceCredentials());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function openRevoke(row: ServiceCredential): void {
    setTarget(row);
    setReason('');
    setDialogError(null);
    setNotice(null);
  }

  async function submitRevoke(): Promise<void> {
    if (!target) return;
    const r = reason.trim();
    if (!r) {
      setDialogError('A reason is required. It is kept with the credential and in the audit trail.');
      return;
    }
    setSubmitting(true);
    setDialogError(null);
    try {
      const updated = await revokeServiceCredential(target.id, r.slice(0, REVOKE_REASON_MAX));
      setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      setNotice(`Revoked ${updated.instanceLabel} (${updated.tokenPrefix}). It stops authenticating now.`);
      setTarget(null);
    } catch (err) {
      setDialogError(revokeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Service credentials"
        subtitle="Credentials that gateways, DSAR connectors, the FOCTTA Core integration and web proxies use to call this tenant. Only the first characters of each are kept."
      />

      {notice ? (
        <GlassCard className="mb-4">
          <div className="p-4 flex items-center gap-2 text-sm" role="status">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span>{notice}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setNotice(null)}>
              Dismiss
            </Button>
          </div>
        </GlassCard>
      ) : null}

      <GlassCard>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {rows.length} credential{rows.length === 1 ? '' : 's'}
              </CardTitle>
              <CardDescription>
                Issue and rotate are not on this page yet; they are done through the API.
              </CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={loading} aria-label="Refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <div className="px-6 pb-6 overflow-x-auto">
          {loadError ? (
            <p className="text-sm text-destructive" role="alert">
              <AlertTriangle className="-mt-0.5 mr-1 inline h-4 w-4" />
              {loadError}
            </p>
          ) : rows.length === 0 && !loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No service credentials have been issued for this tenant.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="pb-3 pr-4 font-medium">Label</th>
                  <th className="pb-3 pr-4 font-medium">Purpose</th>
                  <th className="pb-3 pr-4 font-medium">Token prefix</th>
                  <th className="pb-3 pr-4 font-medium">Status</th>
                  <th className="pb-3 pr-4 font-medium">Created</th>
                  <th className="pb-3 pr-4 font-medium">Expires</th>
                  <th className="pb-3 pr-4 font-medium">Last used</th>
                  <th className="pb-3 pr-4 font-medium">Revoked</th>
                  <th className="pb-3 pr-4 font-medium">Reason</th>
                  <th className="pb-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const badge = STATUS_BADGE[row.status];
                  const Icon = badge.icon;
                  const canRevoke = isRevocable(row.status) && mayRevokeServiceCredential(row.purpose, caller);
                  return (
                    <tr key={row.id} className="border-b last:border-0 align-top" data-testid={`service-credential-${row.id}`}>
                      <td className="py-3 pr-4 font-medium">
                        <KeyRound className="-mt-0.5 mr-1 inline h-3 w-3 text-foreground/60" />
                        {row.instanceLabel}
                      </td>
                      <td className="py-3 pr-4">{PURPOSE_LABEL[row.purpose] ?? row.purpose}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{row.tokenPrefix}</td>
                      <td className="py-3 pr-4">
                        <Badge variant={badge.variant} className="text-[10px]">
                          <Icon className="-mt-0.5 mr-1 inline h-3 w-3" />
                          {badge.label}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-xs">{ist(row.createdAt)}</td>
                      <td className="py-3 pr-4 text-xs">{row.expiresAt ? ist(row.expiresAt) : 'Never'}</td>
                      <td className="py-3 pr-4 text-xs">{row.lastUsedAt ? ist(row.lastUsedAt) : 'Never used'}</td>
                      <td className="py-3 pr-4 text-xs">
                        {row.revokedAt ? (
                          <>
                            {ist(row.revokedAt)}
                            <span className="block text-muted-foreground" title={row.revokedBy ?? undefined}>
                              by {row.revokedByName ?? row.revokedBy ?? 'unknown'}
                            </span>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 pr-4 text-xs break-words max-w-[16rem]">{row.revocationReason ?? '-'}</td>
                      <td className="py-3 text-right">
                        {canRevoke ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openRevoke(row)}
                            data-action="disc.adm_service_creds.a01"
                          >
                            <Ban className="mr-1 h-3 w-3" />
                            Revoke
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </GlassCard>

      {target ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) setTarget(null);
          }}
        >
          <GlassCard className="w-full max-w-md" role="dialog" aria-label="Revoke service credential">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ban className="h-5 w-5" />
                Revoke {target.instanceLabel}
              </CardTitle>
              <CardDescription>
                {PURPOSE_LABEL[target.purpose] ?? target.purpose} credential {target.tokenPrefix}. It stops
                authenticating at once and cannot be restored; the instance using it needs a new credential.
              </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6 space-y-4">
              <label className="block text-sm">
                Reason (required)
                <textarea
                  className="mt-1 w-full rounded-md border border-border bg-background/40 p-2 text-sm"
                  rows={3}
                  maxLength={REVOKE_REASON_MAX}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={submitting}
                  aria-label="Reason"
                />
              </label>
              {dialogError ? (
                <p className="text-sm text-destructive" role="alert">
                  {dialogError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setTarget(null)} disabled={submitting}>
                  Cancel
                </Button>
                <Button onClick={() => void submitRevoke()} disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Revoke credential
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      ) : null}
    </PageContainer>
  );
}

export default ServiceCredentials;
